import type { EventInviteeDto, InviteeResponse } from '@chat-emp/shared';

import { plural } from '../util/format.js';

/**
 * What the guest list says, as words — a separate axis from the appointment's
 * own state (see `statusLabel`), and deliberately its own vocabulary.
 *
 * The appointment answers "is this happening"; an invitee answers "am I
 * coming". Overloading one for the other is how a card ends up reading
 * "Awaiting reply · Awaiting reply" with the two halves meaning different
 * things to different readers.
 */

/**
 * One person's answer, or nothing.
 *
 * `no_response` is a word rather than an absence, unlike `statusLabel`'s
 * silence on `confirmed`. The two rules are opposite on purpose: an appointment
 * that is confirmed needs no comment, but a guest who has not answered is
 * exactly the guest the employer is about to chase, so the list has to name
 * them. `accepted` still prints, because in a column of twenty-four it is the
 * only thing separating the people who are coming from the people who are not.
 */
const RESPONSES: Record<InviteeResponse, string> = {
  no_response: 'Awaiting',
  accepted: 'Accepted',
  rejected: 'Declined',
};

/**
 * "Declined", not "Rejected".
 *
 * The wire word is `rejected` because calendar-service says so; what a person
 * reads is what ajt2.5's live interview invite already says, and one vocabulary
 * across both invite kinds is worth the translation happening here.
 */
export function responseLabel(response: InviteeResponse): string {
  return RESPONSES[response];
}

/**
 * How far the answers have got: "18 accepted, 6 awaiting".
 *
 * Null until somebody has actually answered. A freshly booked Chat Day whose
 * tally reads "24 awaiting" is a line of ink reporting that nothing has
 * happened yet — and the appointment's own status already says "Awaiting reply"
 * beside it. The tally earns its place the moment the two numbers disagree.
 *
 * Zero-count segments are dropped rather than printed as "0 declined": a
 * category with nobody in it is not a fact about this Chat Day.
 */
export function replyTally(invitees: EventInviteeDto[]): string | null {
  let accepted = 0;
  let declined = 0;
  for (const invitee of invitees) {
    if (invitee.response === 'accepted') accepted += 1;
    else if (invitee.response === 'rejected') declined += 1;
  }
  if (accepted + declined === 0) return null;

  const awaiting = invitees.length - accepted - declined;
  const parts: string[] = [];
  if (accepted > 0) parts.push(`${accepted} accepted`);
  if (declined > 0) parts.push(`${declined} declined`);
  if (awaiting > 0) parts.push(`${awaiting} awaiting`);
  return parts.join(', ');
}

/** How many are coming, in the word this product uses for them everywhere else. */
export function guestCount(invitees: EventInviteeDto[]): string {
  return plural(invitees.length, 'guest');
}

/**
 * The faces, in the shape `AvatarStack` draws.
 *
 * `userId` and not the row id: the stack keys on `id`, and the row id is absent
 * on a legacy appointment while the Stream member id never is.
 */
export function facesOf(invitees: EventInviteeDto[]): { id: string; name: string }[] {
  return invitees.map((invitee) => ({ id: invitee.userId, name: invitee.name }));
}
